import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { selectHasLoadError, selectIsLoadingProject } from '@ff-os/pdp-shared-redux/slices/projectLoadingSlice';
import { selectHasPushError } from '@ff-os/pdp-shared-redux/slices/snapshotSlice';
import { Alert } from '@ff-os/shared-react/components/alert/Alert';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { Grid } from '@ff-os/shared-react/components/grid/Grid';
import { Spinner } from '@ff-os/shared-react/components/loaders/spinner/Spinner';
import { WindowStackProvider } from '@ff-os/shared-react/context/window-stack/WindowStack';
import { scrollIntoViewY } from '@ff-os/shared-react/dom/scrollIntoViewY';
import { usePrevious } from '@ff-os/shared-react/hooks/usePrevious';
import { selectActiveVariantId } from '@ff-scene-plugins/gift-redux/selectors/variantSelectors';
import { selectHasPinnedVariables, selectNonPinnedVariables, selectPinnedVariables } from '@ff-scene-plugins/gift-redux/slices/variablesSlice';
import { GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { GiftJson } from '@ff-scene-plugins/gift-shared/GiftJson';
import { selectActivePrefabId } from '../_redux-meta';
import { selectHasMultiplePrefabs } from '../_redux-prefabs';
import { selectShowProjectIsIncompatible } from '../_redux-ui';
import { GiftOrderButton } from '../order-button/GiftOrderButton';
import { PrefabPicker } from '../prefab-picker/PrefabPicker';
import { ArQrCodeStackItem } from '../preview/ArQrCodeStackItem';
import { NonPinnedVariables } from './NonPinnedVariables';
import { PinnedVariables } from './PinnedVariables';
import styles from './InlineGiftDesignerUi.module.scss';

const _InlineGiftDesignerUi = ({
    isLoadingProject, hasLoadError,
    hasPrefabs, hasPushError,
    prefabId, variantId,
    hasPinnedVariables,
    showProjectIsIncompatible
}: Props) => {

    const { t } = useTranslation();
    const containerToScrollTo = useRef(null);
    useScrollVariablesIntoViewOnce(prefabId, variantId, containerToScrollTo);

    return (
        <WindowStackProvider>
            <div className={styles.container}>
                <Grid gap='s' direction='column'>

                    {hasLoadError && (
                        <Cell>
                            <Alert icon='alert-triangle-exclamation' type='error'>
                                {t('load_project.error_message')}
                            </Alert>
                        </Cell>
                    )}

                    {showProjectIsIncompatible && (
                        <Cell>
                            <Alert icon='alert-triangle-exclamation' type='warning'>
                                {t('load_project.incompatible_message')}
                            </Alert>
                        </Cell>
                    )}

                    {hasPinnedVariables && (
                        <Cell>
                            <Grid gap='s' direction='column' className={styles.pinnedVariablesGrid}>
                                <PinnedVariables />
                            </Grid>
                        </Cell>
                    )}

                    {hasPrefabs && (
                        <Cell>
                            <PrefabPicker />
                        </Cell>
                    )}

                    <Cell>
                        <div ref={containerToScrollTo}>
                            <Grid gap='s' direction='column'>
                                <NonPinnedVariables />
                            </Grid>
                        </div>
                    </Cell>

                    <Cell>
                        <GiftOrderButton className='idt-button--product-page' />
                    </Cell>

                    {hasPushError && (
                        <Cell>
                            <Alert icon='alert-triangle-exclamation' type='error'>
                                {t('order_button.error_message')}
                            </Alert>
                        </Cell>
                    )}
                </Grid>

                {isLoadingProject && (
                    <Spinner overlaySize='full' spinnerSize='medium' backgroundColor='white' />
                )}
            </div>

            <ArQrCodeStackItem />
        </WindowStackProvider>
    );
};

type Props = {
    pinnedVariables: GiftJson.Variable<any>[];
    nonPinnedVariables: GiftJson.Variable<any>[];
    hasPinnedVariables: boolean;
    hasPrefabs: boolean;
    isLoadingProject: boolean;
    hasLoadError: boolean;
    hasPushError: boolean;
    prefabId: string | undefined;
    variantId: string;
    showProjectIsIncompatible: boolean;
};

const mapStateToProps = (state: GiftPdpState) => ({
    pinnedVariables: selectPinnedVariables(state),
    nonPinnedVariables: selectNonPinnedVariables(state),
    hasPinnedVariables: selectHasPinnedVariables(state),
    hasPrefabs: selectHasMultiplePrefabs(state),
    isLoadingProject: selectIsLoadingProject(state),
    hasLoadError: selectHasLoadError(state),
    hasPushError: selectHasPushError(state),
    prefabId: selectActivePrefabId(state),
    variantId: selectActiveVariantId(state),
    showProjectIsIncompatible: selectShowProjectIsIncompatible(state)
});

export const InlineGiftDesignerUi = connect(
    mapStateToProps
)(_InlineGiftDesignerUi);

const useScrollVariablesIntoViewOnce = (
    prefabId: string | undefined,
    variantId: string,
    targetRef: MutableRefObject<any>
) => {

    const [hasScrolled, setHasScrolled] = useState(false);
    const previousPrefabId = usePrevious(prefabId);
    const previousVariantId = usePrevious(variantId);

    useEffect(() => {

        if (hasScrolled || !targetRef.current) {
            return;
        }

        if (previousPrefabId !== prefabId && previousVariantId === variantId) {

            setTimeout(() => {
                scrollIntoViewY(targetRef.current, {
                    onlyIfNeeded: true,
                    behavior: 'smooth'
                });
            }, 50);

            setHasScrolled(true);
        }
    }, [
        prefabId, variantId,
        previousVariantId, previousPrefabId,
        hasScrolled, targetRef
    ]);
};